import React, { useMemo, useState } from "react";

import {
  Button,
  Card,
  Layout,
  Page,
  ResourceItem,
  ResourceList,
  Stack,
  TextStyle,
} from "@shopify/polaris";

import { useAdmin } from "context/admin";
import LoadingPage from "shared/LoadingPage";

import CategoryVariantWeightsSection from "./CategoryVariantWeightsSection";
import DepartmentVariantWeightsSection from "./DepartmentVariantWeightsSection";
import PerItemOverrideModal from "./PerItemOverrideModal";
import ProductPickerModal from "./ProductPickerModal";
import SubdepartmentVariantWeightsSection from "./SubdepartmentVariantWeightsSection";
import { cleanDescriptorsForSave } from "./weightOverridePayloads";

const overrideEntries = (overrides) =>
  Object.keys(overrides || {})
    .sort()
    .map((plu) => ({
      plu,
      ...(overrides[plu] || {}),
      descriptors: Array.isArray(overrides[plu]?.descriptors)
        ? overrides[plu].descriptors
        : [],
    }));

// PLU keys remain digits-only (set by ProductPickerModal) and are NOT
// uppercased — only grouping keys flow through normalizeGroupingKey.  The
// descriptor cleanup is identical across all three override types.
const cleanOverrideForSave = ({ label, descriptors }) => ({
  label: String(label || "").trim(),
  descriptors: cleanDescriptorsForSave(descriptors),
});

export default function WeightOptionsPage() {
  const admin = useAdmin();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeOverride, setActiveOverride] = useState(null);
  const [saving, setSaving] = useState(false);

  const shopConfig = admin.selectedShop?.shop_config;
  const overrides = shopConfig?.globepos_sku_weight_overrides || {};
  const entries = useMemo(() => overrideEntries(overrides), [overrides]);

  if (!admin.selectedShop || !shopConfig) {
    return <LoadingPage />;
  }

  const saveShopConfig = (partialConfig) => {
    setSaving(true);
    return admin.updateShopConfig(partialConfig).finally(() => setSaving(false));
  };

  const handleSaveDepartmentWeights = (partialConfig) =>
    saveShopConfig(partialConfig);

  const handleResetDepartmentWeights = (field) => {
    saveShopConfig({ [field]: [] });
  };

  const handleSelectProduct = (product) => {
    setPickerOpen(false);
    setActiveOverride({
      plu: product.plu,
      label: product.productTitle || "",
      descriptors: [{ weight: "", description: "" }],
      productTitle: product.productTitle,
      productImage: product.productImage,
    });
  };

  const handleEditOverride = (entry) => {
    setActiveOverride({
      plu: entry.plu,
      label: entry.label || "",
      descriptors: entry.descriptors,
      productTitle: entry.label || entry.plu,
    });
  };

  const handleSaveOverride = (override) => {
    const cleaned = cleanOverrideForSave(override);
    const nextOverrides = {
      ...overrides,
      [override.plu]: cleaned,
    };
    return saveShopConfig({ globepos_sku_weight_overrides: nextOverrides }).then(
      () => setActiveOverride(null)
    );
  };

  const handleDeleteOverride = (plu) => {
    admin.setConfirmation({
      title: "Remove weight override",
      content:
        "This product will use its department default weight options on the next import.",
      confirmText: "Remove",
      handleOk: () => {
        const nextOverrides = { ...overrides };
        delete nextOverrides[plu];
        return saveShopConfig({ globepos_sku_weight_overrides: nextOverrides });
      },
    });
  };

  const renderOverride = (entry) => {
    const label = entry.label || "Untitled override";
    const descriptorLabel =
      entry.descriptors.length > 0
        ? entry.descriptors
            .map((descriptor) =>
              descriptor.description
                ? `${descriptor.weight} - ${descriptor.description}`
                : descriptor.weight
            )
            .join(", ")
        : "No weight tiers";

    return (
      <ResourceItem id={entry.plu} accessibilityLabel={`Edit ${entry.plu}`}>
        <Stack alignment="center" distribution="equalSpacing" wrap={false}>
          <Stack.Item fill>
            <Stack vertical spacing="extraTight">
              <TextStyle variation="strong">{label}</TextStyle>
              <TextStyle variation="subdued">Base PLU: {entry.plu}</TextStyle>
              <TextStyle variation="subdued">{descriptorLabel}</TextStyle>
            </Stack>
          </Stack.Item>
          <Stack spacing="tight">
            <Button size="slim" onClick={() => handleEditOverride(entry)}>
              Edit
            </Button>
            <Button
              size="slim"
              destructive
              onClick={() => handleDeleteOverride(entry.plu)}
            >
              Remove
            </Button>
          </Stack>
        </Stack>
      </ResourceItem>
    );
  };

  return (
    <Page
      title="Weight Options"
      primaryAction={{
        content: "Add product override",
        onAction: () => setPickerOpen(true),
        loading: saving,
      }}
    >
      <Layout>
        <Layout.Section>
          <DepartmentVariantWeightsSection
            shopConfig={shopConfig}
            onSave={handleSaveDepartmentWeights}
            onReset={handleResetDepartmentWeights}
          />
        </Layout.Section>

        <Layout.Section>
          <SubdepartmentVariantWeightsSection
            shopConfig={shopConfig}
            shopId={admin.selectedShop.id}
            onSave={saveShopConfig}
          />
        </Layout.Section>

        <Layout.Section>
          <CategoryVariantWeightsSection
            shopConfig={shopConfig}
            shopId={admin.selectedShop.id}
            onSave={saveShopConfig}
          />
        </Layout.Section>

        <Layout.Section>
          <Card>
            <Card.Header
              title="Per-item overrides"
              actions={[
                {
                  content: "Add product override",
                  onAction: () => setPickerOpen(true),
                },
              ]}
            />
            <Card.Section>
              <TextStyle variation="subdued">
                Overrides replace department default weights for the matching
                base PLU during GlobePOS import.
              </TextStyle>
            </Card.Section>
            {entries.length === 0 ? (
              <Card.Section>
                <TextStyle variation="subdued">
                  No per-item weight overrides configured.
                </TextStyle>
              </Card.Section>
            ) : (
              <ResourceList
                resourceName={{ singular: "override", plural: "overrides" }}
                items={entries}
                renderItem={renderOverride}
              />
            )}
          </Card>
        </Layout.Section>
      </Layout>

      <ProductPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleSelectProduct}
      />

      <PerItemOverrideModal
        open={Boolean(activeOverride)}
        override={activeOverride}
        onClose={() => setActiveOverride(null)}
        onSave={handleSaveOverride}
      />
    </Page>
  );
}
